import {
  buildPredictionPrompt,
  type PredictionPageConfig,
} from "./prediction-page";

export const WORLD_CUP_PREDICTION_PATH = "/use-cases/world-cup-prediction";

export const worldCupStages = [
  { value: "group-stage", label: "Group Stage" },
  { value: "round-of-32", label: "Round of 32" },
  { value: "round-of-16", label: "Round of 16" },
  { value: "quarter-final", label: "Quarter-final" },
  { value: "semi-final", label: "Semi-final" },
  { value: "third-place", label: "Third-place match" },
  { value: "final", label: "Final" },
] as const;

export type WorldCupStage = (typeof worldCupStages)[number]["value"];

export const worldCupPredictionLenses = [
  {
    value: "match-outcome",
    label: "Match outcome",
    description: "Forecast the likely match path and result.",
    prompt:
      "the likely match trajectory and result, without inventing betting odds",
  },
  {
    value: "turning-points",
    label: "Turning points",
    description: "Find the tactical and personnel moments that can change the game.",
    prompt:
      "the tactical, personnel, and game-state turning points most likely to change the result",
  },
  {
    value: "narrative-reaction",
    label: "Media & fan reaction",
    description: "Trace how the result could reshape the tournament narrative.",
    prompt:
      "how media, supporters, players, and tournament narratives are likely to react after the match",
  },
] as const;

export type WorldCupPredictionLens =
  (typeof worldCupPredictionLenses)[number]["value"];

export const worldCupFormatFacts = [
  { value: "48", label: "teams" },
  { value: "12", label: "groups of four" },
  { value: "104", label: "matches" },
  { value: "3", label: "host countries" },
];

export const worldCupReportOutputs = [
  {
    title: "A likely match path",
    description:
      "See how the game could move from opening shape to decisive phase, with the assumptions made explicit.",
  },
  {
    title: "The factors that matter",
    description:
      "Separate strong signals from narrative noise across tactics, personnel, form, and match context.",
  },
  {
    title: "What could reverse it",
    description:
      "Identify the injury, lineup, tactical, or game-state evidence that would change the forecast.",
  },
];

export const worldCupPredictionFaq = [
  {
    question: "How does the 2026 World Cup prediction tool work?",
    answer:
      "Choose a tournament stage and two teams, then add current scouting reports, match notes, or a public source link. MiroFish turns those materials into a graph of actors and constraints before generating an evidence-led scenario report.",
  },
  {
    question: "Does MiroFish use live scores or betting odds?",
    answer:
      "No. This page does not provide live scores, bookmaker odds, or a real-time sports data feed. Add current source material before each run so the forecast reflects the information you want it to consider.",
  },
  {
    question: "What can I upload for a World Cup prediction?",
    answer:
      "You can upload PDF, Markdown, or plain-text files, or import a public HTTPS document link. Useful sources include scouting reports, lineup notes, injury updates, tactical analysis, and your own match brief.",
  },
  {
    question: "Can this predict the exact score?",
    answer:
      "A report can describe a plausible result, but it should not be treated as a guaranteed scoreline. MiroFish is designed for inspectable scenario analysis: it shows the reasoning, assumptions, and evidence that could change the outcome.",
  },
  {
    question: "Is MiroFish affiliated with FIFA?",
    answer:
      "No. MiroFish is an independent simulation product and is not affiliated with, sponsored by, or endorsed by FIFA. Tournament facts link back to official FIFA sources for verification.",
  },
];

export const WORLD_CUP_FORMAT_SOURCE =
  "https://gpcustomersupportfwc2026.tickets.fifa.com/hc/en-gb/articles/28784798873117-10-What-is-the-format-for-the-FIFA-World-Cup-2026-tournament";

export const WORLD_CUP_HOST_SOURCE =
  "https://gpcustomersupportfwc2026.tickets.fifa.com/hc/en-gb/articles/28783291386653-1-When-and-where-is-the-FIFA-World-Cup-2026-being-held";

export const worldCupPredictionPageConfig = {
  version: 1,
  key: "world-cup",
  path: WORLD_CUP_PREDICTION_PATH,
  analyticsPrefix: "world_cup_prediction",
  testIdPrefix: "world-cup",
  breadcrumbLabel: "World Cup Prediction",
  domainIcon: "sports",
  seo: {
    title: "World Cup Prediction Tool 2026 | MiroFish",
    description:
      "Build a 2026 World Cup prediction from scouting reports, news, and match context. Simulate turning points and generate an evidence-led forecast.",
    keywords: [
      "world cup prediction",
      "2026 world cup prediction",
      "AI world cup predictor",
      "world cup match prediction",
      "world cup prediction tool",
      "MiroFish",
    ],
    applicationName: "MiroFish World Cup Prediction Tool",
    applicationCategory: "SportsApplication",
    featureList: [
      "Evidence-led match scenario setup",
      "Source document and public link import",
      "Turning-point and confidence-condition analysis",
      "Inspectable prediction report workflow",
    ],
  },
  hero: {
    eyebrow: "2026 match intelligence lab",
    title: "Build a World Cup Prediction From Your Own Evidence",
    description:
      "Choose a 2026 matchup, add scouting reports or current match context, and run a MiroFish simulation that explains the likely result, decisive moments, and what could reverse the forecast.",
    primaryCta: "Build my prediction",
    secondaryCta: "See how it works",
    boundary:
      "No live scores or bookmaker odds. Each forecast is an inspectable scenario built from the material you provide.",
    imageSrc: "/images/mirofish/world-cup-match-analysis-hero-v2.webp",
    imageAlt:
      "Photorealistic illustration of two footballers contesting the ball during a night match under stadium floodlights.",
    imageObjectPosition: "54% 50%",
    viewLabel: "Match intelligence view",
    scopeFallback: "Match",
    scenarioLabel: "Selected scenario",
    subjectAFallback: "FIRST TEAM",
    subjectBFallback: "OPPONENT",
    subjectConnector: "vs",
    signalLabel: "Evidence-led",
    provenanceLabel: "Illustrative scene",
  },
  facts: {
    items: worldCupFormatFacts,
    note: "Official 2026 format context, not a live tournament feed.",
    sources: [
      { label: "FIFA format", href: WORLD_CUP_FORMAT_SOURCE },
      { label: "Dates & hosts", href: WORLD_CUP_HOST_SOURCE },
    ],
  },
  lab: {
    eyebrow: "Prediction lab",
    title: "Create your 2026 World Cup prediction",
    description:
      "Define the fixture first. Then give the simulation enough current evidence to make its assumptions visible and reviewable.",
    setupEyebrow: "Match setup",
    setupTitle: "Set the fixture",
    scopeLabel: "Tournament stage",
    defaultScope: "group-stage",
    scopes: [...worldCupStages],
    subjectA: { label: "First team", placeholder: "Enter a team" },
    subjectB: { label: "Opponent", placeholder: "Enter the opponent" },
    duplicateSubjectsError: "Choose two different teams for this matchup.",
    lensLabel: "Prediction focus",
    defaultLens: "match-outcome",
    lenses: [...worldCupPredictionLenses],
    contextLabel: "Extra context",
    contextPlaceholder:
      "Add a tactical question, lineup assumption, or condition the report should test.",
    evidenceEyebrow: "Evidence input",
    evidenceTitle: "Add current sources",
    dropTitle: "Drop match evidence here",
    publicLinkLabel: "Public HTTPS document link",
    publicLinkPlaceholder: "https://example.com/match-report.pdf",
    publicLinkHelp:
      "The link must resolve to a public PDF, Markdown, or text file.",
    preparingUploadLabel: "Preparing match evidence...",
    preparingLinkLabel: "Downloading the linked match brief...",
    launchLabel: "Run the match simulation",
    outputDisclaimer: "Predictions are exploratory outputs, not betting advice.",
  },
  report: {
    eyebrow: "Report structure",
    title: "What a World Cup prediction should explain",
    description:
      "A useful forecast is more than a team name or score. It should expose the match path, the evidence behind it, and the point at which the answer stops being reliable.",
    outputs: worldCupReportOutputs,
  },
  workflow: {
    eyebrow: "Evidence before confidence",
    title: "From match brief to evidence-led forecast",
    steps: [
      {
        icon: "evidence",
        title: "Bring current evidence",
        description:
          "Add scouting notes, tactical analysis, lineup reports, or a public document link.",
      },
      {
        icon: "world",
        title: "Build the match world",
        description:
          "MiroFish maps teams, players, constraints, incentives, and narrative pressure into a reviewable graph.",
      },
      {
        icon: "forecast",
        title: "Interrogate the forecast",
        description:
          "Read the likely path, inspect turning points, and ask what new evidence would change the result.",
      },
    ],
    boundaryTitle: "Know the boundary",
    boundaryDescription:
      "MiroFish is not a live sports feed and does not promise an exact score. Treat each report as a scenario to inspect, update, and challenge when lineups, injuries, tactics, or match conditions change.",
  },
  faq: {
    eyebrow: "World Cup prediction FAQ",
    title: "Questions before kickoff",
    items: worldCupPredictionFaq,
    footnote:
      "MiroFish is independent and is not affiliated with, sponsored by, or endorsed by FIFA.",
  },
  prompt: {
    subjectAFallback: "Team A",
    subjectBFallback: "Team B",
    introduction:
      "Build an evidence-led 2026 World Cup prediction for the {{scope}} match between {{subjectA}} and {{subjectB}}.",
    evidenceInstruction:
      "Use the attached source material as the primary evidence and distinguish reported facts from inference. Focus on {{lens}}.",
    outputInstruction:
      "Return: (1) the likely match trajectory and result, (2) three decisive factors, (3) plausible turning points, (4) the conditions behind the confidence level, and (5) the new evidence most likely to reverse the forecast.",
    boundaryInstruction:
      "Do not present certainty, bookmaker odds, or betting advice.",
    contextPrefix: "Additional context:",
  },
} satisfies PredictionPageConfig;

export const buildWorldCupPredictionPrompt = ({
  stage,
  teamA,
  teamB,
  lens,
  context,
}: {
  stage: WorldCupStage;
  teamA: string;
  teamB: string;
  lens: WorldCupPredictionLens;
  context?: string;
}) => {
  return buildPredictionPrompt(worldCupPredictionPageConfig, {
    scope: stage,
    subjectA: teamA,
    subjectB: teamB,
    lens,
    context,
  });
};
